import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import {
    leaderboardFavouriteSelector,
    leaderboardFilterSelector
} from 'common/store/modules/player-awards/leaderboard/selectors';

import {
    useGetBrownlowPredictorSearchQuery,
    useGetBrownlowPredictorFavouritesQuery,
    useGetTeamsQuery
} from 'common/store/modules/api/afl-api';

import { useBettingVis } from 'common/react/hooks/use-betting-vis';
import {
    useGetBFAwardsLeaderboardQuery,
    useGetBrownlowBettingOddsQuery
} from 'common/store/modules/api/cfs-api';

import { useCurrentPlayerPulseIds } from 'widgets/player-awards-tracker/js/components/Leaderboard/hooks/use-current-player-pulse-ids';

import {
    assignPositions,
    composeColumnData,
    composePredictorRowData
} from 'widgets/player-awards-tracker/js/components/Leaderboard/components/table/utils';

import { navSelector } from 'common/store/modules/competition-nav/selectors';
import { leaderboardSelector } from 'common/store/modules/player-awards/leaderboard/selectors/simple-selectors';

import { useBrownlowPlayers } from './use-brownlow-players';

const PAGE_SIZE = 20;

/**
 *
 * @param playersData
 * @param teams
 * @param shouldShowBettingOdds
 * @param bettingOddsData
 */
function mapPlayersForTable(
    playersData = [],
    teams,
    shouldShowBettingOdds,
    bettingOddsData
) {
    return (
        playersData?.map((item) => {
            const roundByRoundVotes = Object.keys(item.rounds).map((key) => ({
                matchId: '',
                roundNumber: key,
                votes: item.rounds[key]?.points ?? ''
            }));

            const teamObject = teams.find(
                (teamItem) => teamItem.id === item.teamId
            );

            return {
                eligible: item.eligible ?? false,
                player: { ...item },
                roundByRoundVotes,
                team: {
                    teamAbbr: teamObject?.abbreviation,
                    teamId: teamObject?.providerId,
                    teamName: teamObject?.name,
                    teamNickname: teamObject?.nickname
                },
                totalVotes: item.totalVotes ?? '',
                bettingOdds: shouldShowBettingOdds
                    ? bettingOddsData?.outcomes?.find(
                          (outcome) => item?.providerId === outcome?.playerId
                      )?.price ?? null
                    : null,
                bettingOddsUrl: shouldShowBettingOdds
                    ? bettingOddsData?.url ?? null
                    : null
            };
        }) ?? []
    );
}

/**
 *
 * @param seasonId
 */
export function useBrownlowPredictorData(seasonId) {
    const favourites = useSelector(leaderboardFavouriteSelector);

    const leaderboardFilter = useSelector(leaderboardFilterSelector);
    const nav = useSelector(navSelector);
    const seasons = nav?.seasons;

    const season = seasons?.find((item) => item.id === nav.season.id);
    const seasonPrId = season?.providerId ?? '';

    const currentRoundNumber = season?.currentRoundNumber;

    const rounds = nav?.rounds;

    const { data: teamsData } = useGetTeamsQuery(nav.season.id, {
        skip: !nav.season.id
    });

    const leaderboardState = useSelector(leaderboardSelector);

    // Avoid re-creating primitive values unless they actually change
    const teamsList = teamsData || [];
    const selectedTeam = teamsList.find(
        (item) => item.id === leaderboardState.team.id
    );
    const teams = teamsList;
    const teamId = selectedTeam?.providerId ?? '';
    const teamFilter = leaderboardState?.team.id;

    const [noDataAvailable, setNoDataAvailable] = useState(false);
    const [currentFavouriteIds, setCurrentFavouriteIds] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);

    const fetchMoreData = () => setCurrentPage((prevPage) => prevPage + 1);

    const { data: bettingOddsData } = useGetBrownlowBettingOddsQuery(null, {
        pollingInterval: PULSE.app.common.CONSTANTS.TIME.ONE_MINUTE_IN_MS
    });

    const shouldShowBettingOdds =
        useBettingVis() &&
        bettingOddsData?.displayChannels?.web === true &&
        bettingOddsData?.outcomes?.length > 0 &&
        season?.id === Number(seasonId);

    const teamIdFromFilters = teamFilter !== -1 ? teamFilter.toString() : '';

    const [
        players = [],
        isLoading,
        isFetching,
        isPredictorError,
        hideLoadMoreBtn
    ] = useBrownlowPlayers(
        season ? season.id : null,
        currentPage - 1,
        teamIdFromFilters,
        PAGE_SIZE
    );

    // Favourite players rows (for pulse IDs)
    const favouriteRows = useMemo(() => {
        const rows = favourites.map((player) => ({
            player: {
                ...player,
                playerId: player.providerId
            }
        }));
        return { rows, pageSize: rows.length };
    }, [favourites]);

    useCurrentPlayerPulseIds({
        rowData: favouriteRows.rows,
        currentPlayerIds: currentFavouriteIds,
        setCurrentPlayerIds: setCurrentFavouriteIds
    });

    const { data: favouritePlayers = [] } =
        useGetBrownlowPredictorFavouritesQuery(
            {
                compSeasonId: season ? season.id : null,
                players: currentFavouriteIds
            },
            {
                skip:
                    !(
                        season?.id &&
                        (leaderboardFilter.query === null ||
                            leaderboardFilter.query === '')
                    ) || favourites.length === 0
            },
            { refetchOnMountOrArgChange: true }
        );

    const {
        data: searchResultPlayers,
        isLoading: isLoadingSearch,
        isFetching: isFetchingSearch,
        isError: isPlayerSearchError
    } = useGetBrownlowPredictorSearchQuery(
        {
            compSeasonId: season ? season.id : null,
            term: leaderboardFilter.query
        },
        {
            skip:
                leaderboardFilter.query === null ||
                leaderboardFilter.query === ''
        }
    );

    const playersToShow = useMemo(() => {
        if (leaderboardFilter.query) {
            return searchResultPlayers?.results;
        }

        const merged = [
            ...players.slice(0, PAGE_SIZE * currentPage),
            ...favouritePlayers
        ];

        return Array.from(
            new Map(merged.map((item) => [item.providerId, item])).values()
        );
    }, [
        currentPage,
        favouritePlayers,
        leaderboardFilter.query,
        players,
        searchResultPlayers?.results
    ]);

    useEffect(() => {
        setNoDataAvailable(false);

        if (
            (!playersToShow || isPlayerSearchError || isPredictorError) &&
            !(isLoading || isLoadingSearch)
        ) {
            setNoDataAvailable(true);
        }
    }, [
        isLoading,
        isLoadingSearch,
        isPlayerSearchError,
        isPredictorError,
        playersToShow
    ]);

    const playersForTable = mapPlayersForTable(
        playersToShow,
        teams,
        shouldShowBettingOdds,
        bettingOddsData
    );

    const playersForTableWihtPos = assignPositions([...playersForTable]);

    const { data: leaderboardData } = useGetBFAwardsLeaderboardQuery(
        {
            seasonId: seasonPrId,
            teamId: teamId ?? null
        },
        { skip: !seasonPrId }
    );

    const columns = useMemo(() => composeColumnData(rounds), [rounds]);

    // Row data
    const rowData = useMemo(() => {
        const predictedFromShownData = playersForTableWihtPos.map(
            (dataObject) => dataObject.player
        );
        return playersForTableWihtPos.map((item) => {
            const yearFromCompSeason = seasonPrId.replace('CD_S', '');
            const playerFromProviderId = item.player.providerId.replace(
                'CD_I',
                ''
            );
            let headshotUrl;

            if (yearFromCompSeason) {
                headshotUrl = `https:${
                    PULSE.app.environment.playerImagePath
                }/${'AFL'}/${yearFromCompSeason}/${playerFromProviderId}.png?im=Scale,width=0.6,height=0.6`;
            }

            return composePredictorRowData(
                { ...item, player: { ...item.player, photoURL: headshotUrl } },
                columns.filter(
                    (col) =>
                        !['player', 'totalVotes', 'bettingOdds'].includes(
                            col.accessor
                        )
                ),
                rounds,
                predictedFromShownData
            );
        });
    }, [playersForTableWihtPos, seasonPrId, columns, rounds]);

    // Reset current page on team and season change
    useEffect(() => {
        setCurrentPage((prev) => (prev !== 1 ? 1 : prev));
    }, [seasonPrId, teamId, setCurrentPage]);

    return {
        isLoading,
        isLoadingSearch,
        noDataAvailable,
        rowData,
        columns,
        favourites,
        isFetching: isFetching || isFetchingSearch,
        hideLoadMoreBtn,
        fetchMoreData,
        seasonPrId,
        shouldShowBettingOdds,
        leaderboardFilter,
        leaderboardData,
        currentRoundNumber
    };
}
